/*
 * Erinnerungen per E-Mail der Verwaltung — was die Oberfläche darüber wissen
 * muss.
 *
 * Gegenstück zu `services/adminReminders/schedule.js` im Backend. Die Wörter
 * (`ASSIGNED_TICKETS`, `OFF`/`DAILY`/`WEEKLY`, Wochentag 1 = Montag … 7 =
 * Sonntag, Stunde 0–23 Wiener Zeit) müssen auf beiden Seiten dieselben sein;
 * wann eine Mail fällig ist, rechnet nur der Server.
 *
 * Bewusst ohne React: Die Umformung zwischen Antwort, Formular und Rumpf lässt
 * sich so ohne Oberfläche prüfen (reminderSettings.test.js).
 */

export const REMINDER_KINDS = {
	ASSIGNED_TICKETS: 'ASSIGNED_TICKETS',
	OPEN_REVIEWS: 'OPEN_REVIEWS'
}

export const REMINDER_FREQUENCIES = ['OFF', 'DAILY', 'WEEKLY']

export const REMINDER_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

export const REMINDER_HOURS = Array.from({ length: 24 }, (_, hour) => hour)

export const DEFAULT_REMINDER = { frequency: 'OFF', weekday: 1, hour: 7, instantOnAssign: true }

export const hourLabel = hour => `${String(hour).padStart(2, '0')}:00`

const zahl = (wert, ersatz, min, max) => {
	const n = Number(wert)
	return wert !== '' && wert !== null && Number.isInteger(n) && n >= min && n <= max ? n : ersatz
}

/** Eine Art aus der Antwort, auf feste Werte gebracht. */
export const toFormValue = setting => ({
	frequency: REMINDER_FREQUENCIES.includes(setting?.frequency) ? setting.frequency : DEFAULT_REMINDER.frequency,
	weekday: zahl(setting?.weekday, DEFAULT_REMINDER.weekday, 1, 7),
	hour: zahl(setting?.hour, DEFAULT_REMINDER.hour, 0, 23),
	instantOnAssign: setting?.instantOnAssign === undefined ? true : setting.instantOnAssign === true
})

/** Beide Arten aus der Antwort von GET /admin/reminder-settings/:id. */
export const toForm = settings => ({
	[REMINDER_KINDS.ASSIGNED_TICKETS]: toFormValue(settings?.[REMINDER_KINDS.ASSIGNED_TICKETS]),
	[REMINDER_KINDS.OPEN_REVIEWS]: toFormValue(settings?.[REMINDER_KINDS.OPEN_REVIEWS])
})

/** Der Rumpf für PUT — das Häkchen gibt es nur bei den Anforderungen. */
export const toPayload = form => {
	const art = value => ({ frequency: value.frequency, weekday: value.weekday, hour: value.hour })
	return {
		[REMINDER_KINDS.ASSIGNED_TICKETS]: {
			...art(form[REMINDER_KINDS.ASSIGNED_TICKETS]),
			instantOnAssign: Boolean(form[REMINDER_KINDS.ASSIGNED_TICKETS].instantOnAssign)
		},
		[REMINDER_KINDS.OPEN_REVIEWS]: art(form[REMINDER_KINDS.OPEN_REVIEWS])
	}
}

/**
 * Hat sich gegenüber dem gespeicherten Stand etwas geändert, das zählt?
 *
 * Bei „aus" zählen Wochentag und Uhrzeit nicht, bei „täglich" nicht der
 * Wochentag — sonst fragte das Fenster beim Schließen nach Änderungen, die gar
 * nichts bewirken.
 */
export const isReminderFormChanged = (gespeichert, aktuell) =>
	Object.values(REMINDER_KINDS).some(kind => {
		const a = gespeichert?.[kind]
		const b = aktuell?.[kind]
		if (!a || !b) return false
		if (a.frequency !== b.frequency) return true
		if (kind === REMINDER_KINDS.ASSIGNED_TICKETS && a.instantOnAssign !== b.instantOnAssign) return true
		if (b.frequency === 'OFF') return false
		if (a.hour !== b.hour) return true
		return b.frequency === 'WEEKLY' && a.weekday !== b.weekday
	})

/**
 * Kurzfassung einer Art für die Liste „Administration": „täglich 07:00",
 * „Mo 07:00" — oder null, wenn sie aus ist.
 *
 * @param t Übersetzerin aus useTranslation
 */
export const reminderShortLabel = (setting, t) => {
	const value = toFormValue(setting)
	if (value.frequency === 'OFF') return null
	const tag = value.frequency === 'DAILY' ? t('reminderDailyShort') : t(`reminderWeekdayShort${value.weekday}`)
	return `${tag} ${hourLabel(value.hour)}`
}

/** Ist für dieses Konto überhaupt eine Erinnerung eingeschaltet? */
export const hasActiveReminder = reminders =>
	Object.values(REMINDER_KINDS).some(kind => toFormValue(reminders?.[kind]).frequency !== 'OFF')
